import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import cn from 'classnames';
import { IRootState } from 'src/redux/rootReducer';
import { NavLink, View } from '../common';
import { SidebarMenu, UserRole } from 'src/appConfig/sidebar';
import { hasPagePermission } from 'src/validations/permissions';
import './styles.scss';
import { IoIosArrowDown } from 'react-icons/io';
import commonActions from 'src/redux/commonRedux/actions';

// More info: https://www.npmjs.com/package/react-pro-sidebar
const Sidebar: React.FC<Props> = ({ authorizedUser, showSidebar, isCollapse, onSetSidebar }) => {
  const filterSidebar = SidebarMenu.filter(item => {
    const pagePermissions = item.permissions;
    const userRole: UserRole = (authorizedUser?.role as UserRole) ?? 'FOREMAN';
    return hasPagePermission(userRole, pagePermissions);
  });

  const renderSidebarItem = (item, idx) => {
    if (item?.subMenu)
      return (
        <SubMenuItem
          key={`sidebar-item-${idx}`}
          item={item}
          idx={idx}
          isCollapse={isCollapse}
          showSidebar={showSidebar}
          onSetSidebar={onSetSidebar}
        />
      );
    return (
      <NavLink
        key={`navigator-bar-${idx}`}
        to={item.href || ''}
        className={cn('cmp-sidebar__item')}
        activeClassName="cmp-sidebar__item--active">
        {item.icon}
        <View className={cn('cmp-sidebar__item__text')}>{item.title}</View>
      </NavLink>
    );
  };

  return (
    <div
      className={cn('cmp-sidebar', {
        'cmp-sidebar--is-show': showSidebar,
        'cmp-sidebar__mini': isCollapse,
      })}>
      <View className={cn('cmp-sidebar__container')}>
        {filterSidebar.map((item, idx) => renderSidebarItem(item, idx))}
      </View>
    </div>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;
const mapStateToProps = (state: IRootState) => ({
  userPermissions: state.auth.permissions,
  isCollapse: state.common.collapseSidebar,
  showSidebar: state.common.showSidebar,
  authorizedUser: state.auth.authUser,
});

const mapDispatchToProps = {
  onSetSidebar: commonActions.setSidebar,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Sidebar));

const SubMenuItem = ({ item, idx, isCollapse, showSidebar, onSetSidebar }) => {
  const [showMenu, setShowMenu] = useState(false);
  const subHref = useMemo(() => item?.subMenu.map(subItem => subItem.href).join(' '), [item]);
  const pathName = useLocation().pathname;

  useEffect(() => {
    if (isCollapse) onSetSidebar(false);
    // eslint-disable-next-line
  }, [pathName]);

  const activeSubMenu = subHref?.match(pathName) || showMenu;
  return (
    <View key={`navigator-bar-${idx}`} className={cn('cmp-sidebar__subMenu')}>
      <View
        className={cn('cmp-sidebar__item', { 'cmp-sidebar__item--active': activeSubMenu })}
        onClick={() => setShowMenu(!showMenu)}>
        {item.icon}
        <View className={cn('cmp-sidebar__item__text')}>{item.title}</View>
        {!isCollapse && (
          <IoIosArrowDown
            className={cn('cmp-sidebar__subMenu__icon', { 'cmp-sidebar__subMenu__icon--up': showMenu })}
          />
        )}
      </View>
      {showMenu && showSidebar && (
        <View className={cn('cmp-sidebar__subMenus', { 'cmp-sidebar__subMenus--collapse': isCollapse })}>
          {item?.subMenu.map((item, index) => {
            return (
              <NavLink
                key={`subMenu-bar-${index}`}
                to={item.href || ''}
                className={cn('cmp-sidebar__item')}
                activeClassName="cmp-sidebar__item--active">
                <View className={cn('cmp-sidebar__item__text')}>{item.title}</View>
              </NavLink>
            );
          })}
        </View>
      )}
    </View>
  );
};
